import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core'
import { Router } from '@angular/router'
import { Message, User } from '@tradecafe/types/core'
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed'
import angular from 'angular'
import { orderBy, reject } from 'lodash-es'
import { BehaviorSubject, EMPTY, from, of, timer } from 'rxjs'
import { catchError, map, pairwise, startWith, switchMap } from 'rxjs/operators'
import { AuthApiService } from 'src/api/auth'
import { MessageApiService } from 'src/api/message'
import { ModalProxyService } from 'src/shared/modal'


const TIME_INTERVAL = 3 * 60 * 1000

@Component({
  selector: 'tc-feeds-bell',
  templateUrl: './feeds-bell.component.html',
  styleUrls: ['./feeds-bell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FeedsBellComponent extends OnDestroyMixin implements OnInit {
  private modalHelper = inject(ModalProxyService)
  private MessageApi = inject(MessageApiService)
  private AuthApi = inject(AuthApiService)
  private router = inject(Router)

  // Preview of the most recent unread messages (shown in the dropdown).
  unreadMessages$ = new BehaviorSubject<Message[]>([])
  // The true unread total (badge), computed server-side — not the preview length.
  unreadCount$ = new BehaviorSubject<number>(0)
  // True while the dropdown preview is being fetched (shows the overlay loader).
  loadingUnread$ = new BehaviorSubject<boolean>(false)

  ngOnInit() {
    this.refreshUnreadCount().pipe(untilComponentDestroyed(this)).subscribe(count => {
      this.unreadCount$.next(count)
    })
  }

  // The preview is loaded lazily when the bell is opened — the poll itself
  // only fetches the count (limit=0), so no message bodies cross the wire
  // every TIME_INTERVAL.
  onMenuOpened() {
    const {account, user_id} = this.AuthApi.currentUser
    this.loadingUnread$.next(true)
    this.MessageApi.getUnread(account).then(r => {
      if (this.AuthApi.currentUser?.user_id !== user_id) return
      this.unreadCount$.next(r.data.count)
      this.unreadMessages$.next(orderBy(r.data.messages, ['created'], ['desc']).map(message => ({
        ...message,
        body_txt: jQuery(`<div>${message.body_txt}</div>`).text(),
      })))
    }).finally(() => {
      this.loadingUnread$.next(false)
    })
  }

  viewAllFeeds() {
    this.router.navigateByUrl('/notification/feeds')
  }

  private refreshUnreadCount() {
    return this.AuthApi.currentUser$.pipe(
      startWith(undefined as User),
      pairwise(),
      switchMap(([previousUser, currentUser]) => {
        const isSameUser = previousUser?.user_id === currentUser?.user_id
        if (!isSameUser) this.unreadMessages$.next([])
        if (!currentUser) return of(0)
        // timer(0, N): fetch immediately on login, then poll every N ms
        // (interval(N) alone would leave the badge empty for the first N ms).
        // limit=0 → count-only: the poll never downloads message bodies.
        const poll = timer(0, TIME_INTERVAL).pipe(
          switchMap(() => this.fetchUnreadCount(currentUser.account)))
        return isSameUser ? poll : poll.pipe(startWith(0))
        }))
  }

  private fetchUnreadCount(account: number) {
    return from(this.MessageApi.getUnread(account, 0)).pipe(
      map(r => r.data.count),
      catchError(() => EMPTY))
  }

  showMessageItem(item: Message) {
    return this.modalHelper.showModal({
      component: 'tcMessageViewForm',
      windowClass: 'modalclone',
      size: 'md',
      backdrop: 'static',
      resolve: {
        message: () => angular.copy(item),
      },
    }).catch(() => {})
    .then(() => {
      const {account} = this.AuthApi.currentUser
      return this.MessageApi.markAsRead(account, [item.message_id]).then(() => {
        this.unreadMessages$.next(reject(this.unreadMessages$.value, { message_id: item.message_id }))
        this.unreadCount$.next(Math.max(0, this.unreadCount$.value - 1))
      })
    })
  }

  markAllAsRead() {
    const {account} = this.AuthApi.currentUser
    this.MessageApi.markAllRead(account).then(() => {
      this.unreadMessages$.next([])
      this.unreadCount$.next(0)
    })
  }

  getMessageId(_index: number, message: Message) {
    return message.message_id
  }
}
